<template>
    <div v-if="show" class="reformatter-overlay" @click.self="close">
      <div class="reformatter-modal">
        <!-- Header -->
        <div class="reformatter-header">
          <h2>
            <i class="fas fa-magic"></i>
            Resume Reformatter - {{ candidateId.slice(0, 10) }}
          </h2>
          <button @click="close" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
  
        <!-- Content -->
        <div class="reformatter-content">
  
            <!-- Loading State -->
            <div v-if="isGenerating" class="loading-state">
              <div class="progress-container">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                </div>
                <p class="progress-text">{{ progressMessage }}</p>
                <p class="progress-percent">{{ progressPercent }}%</p>
              </div>
              <p class="loading-text">Reformatting your resume...</p>
              <p class="loading-subtext">This takes 30-60 seconds</p>
            </div>
          
            <!-- Input State (before generation) -->
            <div v-else-if="!reformattedHtml" class="input-state">
              <div class="form-section">
                <h3>Job Description (Optional)</h3>
                <p class="help-text">
                  Add a job description to reorder sections and highlight relevant experience
                </p>
                <textarea
                  v-model="jobDescription"
                  placeholder="Paste job description here for relevance-based formatting..."
                  class="job-description-textarea"
                  rows="8"
                ></textarea>
              </div>
          
              <div class="form-section">
                <h3>Export Options</h3>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="includeContact">
                  <span>Include contact information</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="includeName">
                  <span>Include candidate name</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="includeLogo">
                  <span>Include company logo</span>
                </label>
              </div>
          
              <button @click="generateReformat" class="btn-primary btn-large">
                <i class="fas fa-rocket"></i>
                Generate Reformatted Resume
              </button>
            </div>
          
            <!-- Preview State (after generation) - ONLY ONE -->
            <div v-else class="preview-state">
              <div class="preview-header">
                <h3>
                  <i class="fas fa-eye"></i>
                  Preview {{ isEditing ? '(Editing Mode)' : '' }}
                </h3>
                <div class="preview-actions">
                  <!-- NEW: Page break preview toggle -->
                  <button @click="togglePageBreakPreview" class="btn-secondary">
                    <i class="fas fa-file-pdf"></i>
                    {{ showPageBreaks ? 'Hide' : 'Show' }} Page Breaks
                  </button>
                  
                  <button @click="toggleEditMode" class="btn-secondary">
                    <i :class="isEditing ? 'fas fa-eye' : 'fas fa-edit'"></i>
                    {{ isEditing ? 'Stop Editing' : 'Edit Content' }}
                  </button>
                  <button @click="regenerate" class="btn-secondary">
                    <i class="fas fa-redo"></i>
                    Regenerate
                  </button>
                </div>
              </div>
<!--  Page break toolbar (only when page breaks shown) -->
<div v-if="showPageBreaks" class="page-break-toolbar">
  <div class="toolbar-info">
    <i class="fas fa-info-circle"></i>
    <span>A4 Page: 297mm tall (~1123px). Red lines show where PDF pages will break.</span>
  </div>
  <button @click="insertPageBreakAtCursor" class="btn-secondary btn-small">
    <i class="fas fa-plus"></i>
    Insert Page Break Here
  </button>
  <button @click="removeAllPageBreaks" class="btn-secondary btn-small">
    <i class="fas fa-trash"></i>
    Remove Manual Breaks
  </button>
</div>



          <!--   Rich Text Toolbar (only shows when editing) -->
          <div v-if="isEditing" class="rich-text-toolbar">
            <div class="toolbar-group">
              <button @click="execCommand('bold')" class="toolbar-btn" title="Bold (Ctrl+B)">
                <i class="fas fa-bold"></i>
              </button>
              <button @click="execCommand('italic')" class="toolbar-btn" title="Italic (Ctrl+I)">
                <i class="fas fa-italic"></i>
              </button>
              <button @click="execCommand('underline')" class="toolbar-btn" title="Underline (Ctrl+U)">
                <i class="fas fa-underline"></i>
              </button>
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
              <button @click="execCommand('justifyLeft')" class="toolbar-btn" title="Align Left">
                <i class="fas fa-align-left"></i>
              </button>
              <button @click="execCommand('justifyCenter')" class="toolbar-btn" title="Align Center">
                <i class="fas fa-align-center"></i>
              </button>
              <button @click="execCommand('justifyRight')" class="toolbar-btn" title="Align Right">
                <i class="fas fa-align-right"></i>
              </button>
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
              <select @change="changeFontSize" class="toolbar-select">
                <option value="">Font Size</option>
                <option value="1">Small</option>
                <option value="3">Normal</option>
                <option value="5">Large</option>
                <option value="7">Extra Large</option>
              </select>
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
              <label class="toolbar-color-picker">
                <i class="fas fa-palette"></i>
                <input type="color" @change="changeTextColor" class="color-input">
              </label>
              <button @click="removeFormatting" class="toolbar-btn" title="Remove Formatting">
                <i class="fas fa-eraser"></i>
              </button>
            </div>
          </div>



              <!-- Editable Preview (contenteditable iframe) -->
              <div class="html-preview">
                <iframe
                  ref="previewFrame"
                  :srcdoc="reformattedHtml"
                  class="preview-iframe"
                  @load="onIframeLoad"
                ></iframe>
              </div>
          
              <div v-if="isEditing" class="edit-hint">
                <i class="fas fa-info-circle"></i>
                Click inside the preview above to edit text directly. Changes will be saved when you export.
              </div>
            </div>
          </div>
  
        <!-- Footer -->
        <div class="reformatter-footer">
          <button @click="close" class="btn-secondary">
            Cancel
          </button>
          <button
            v-if="reformattedHtml"
            @click="exportPDF"
            class="btn-primary"
            :disabled="isExporting"
          >
            <i class="fas fa-file-pdf"></i>
            {{ isExporting ? 'Exporting...' : 'Save as PDF' }}
          </button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'ResumeReformatter',
    props: {
      show: {
        type: Boolean,
        required: true
      },
      candidateId: {
        type: String,
        required: true
      },
      idType: {
        type: String,
        required: true
      }
    },
    
    data() {
      return {
         showPageBreaks: false,        // NEW: Show/hide page break lines
        pageBreakPositions: [],       // NEW: Array of y-positions for breaks
        progressPercent: 0,       
        progressMessage: '',    
        jobDescription: '',
        includeContact: false,
        includeName: false,
        includeLogo: true,
        isCheckingCache: false,  // NEW: Show loading while checking cache

        isGenerating: false,
        isExporting: false,
        
        reformattedHtml: null,
        cachedHtml: null, // Store original generated HTML for regeneration
        showSimpleEditor: false,  // NEW
        readableContent: '',  // NEW: Plain text version
        isEditing: false,  // NEW: Track edit mode



      };
    },

    watch: {
    candidateId(newId, oldId) {
      if (newId !== oldId) {
        console.log('🔄 Candidate changed, resetting state');
        this.resetState();
      }
    },
    
    async show(isShown) {
      if (isShown) {
        console.log('✅ Modal opened for candidate:', this.candidateId);
        // When modal opens, try to load cached version automatically
        await this.tryLoadCachedVersion();
      } else {
        console.log('❌ Modal closed, resetting state');
        this.resetState();
      }
    }
  },
    methods: {
        resetState() {
      console.log('🧹 Resetting all state');
      this.reformattedHtml = null;
      this.cachedHtml = null;
      this.isEditing = false;
      this.isGenerating = false;
      this.isExporting = false;
      this.isCheckingCache = false;
      this.jobDescription = '';
      this.showPageBreaks = false;       
      this.pageBreakPositions = [];   
    },
    async tryLoadCachedVersion() {
      /**
       * When modal opens, check if cache exists and load it
       * IMPORTANT: Use check_cache_only to avoid generating if no cache exists
       */
      this.isCheckingCache = true;
      
      try {
        console.log('🔍 Checking for cached version...');
        
        // STEP 1: Check if cache exists (don't generate!)
        const checkParams = new URLSearchParams();
        checkParams.append('format', 'html');
        checkParams.append('check_cache_only', 'true');  // ← CRITICAL: Only check, don't generate
        checkParams.append('id_type', this.idType);  

        const apiBaseUrl = this.getApiBaseUrl();
        const checkUrl = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat?${checkParams.toString()}`;
        
        const checkResponse = await fetch(checkUrl);
        
        if (checkResponse.ok) {
          // Cache exists! Now fetch it
          console.log('✅ Cache exists, loading it...');
          
          const loadParams = new URLSearchParams();
          loadParams.append('format', 'html');
          loadParams.append('id_type', this.idType);
          // Don't send check_cache_only this time - we want the actual HTML
          
          const loadUrl = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat?${loadParams.toString()}`;
          const loadResponse = await fetch(loadUrl);
          
          if (loadResponse.ok) {
            const html = await loadResponse.text();
            this.reformattedHtml = html;
            this.cachedHtml = html;
            console.log('✅ Auto-loaded cached version');
          }
        } else {
          // No cache exists, show the form (don't generate!)
          console.log('ℹ️ No cached version found - showing form');
        }
      } catch (error) {
        console.error('❌ Error checking cache:', error);
        // On error, just show the form
      } finally {
        this.isCheckingCache = false;
      }
    },
    

  async checkForCachedVersion() {
    // Check if cache exists for this candidate
    try {
      const params = new URLSearchParams();
      params.append('format', 'html');
      params.append('check_cache_only', 'true'); 
      params.append('id_type', this.idType); // NEW flag
      
      if (this.jobDescription.trim()) {
        params.append('job_description', this.jobDescription.trim());
      }
      
      const apiBaseUrl = this.getApiBaseUrl();
      const url = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat?${params.toString()}`;
      
      const response = await fetch(url, { method: 'HEAD' }); // Just check, don't fetch
      
      if (response.ok) {
        console.log('✅ Cached version exists for this candidate');
        // Could show a "Load cached version" button here
      }
    } catch (error) {
      // No cache, that's fine
      console.log('No cached version found');
    }
  },
     

  async generateReformat(forceRegenerate = false) {
  this.isGenerating = true;
  
  try {
    const params = new URLSearchParams();
    params.append('format', 'html');
    params.append('async_mode', 'true');  
    params.append('id_type', this.idType);// ← Enable async mode
    
    if (this.includeContact) params.append('include_contact', 'true');
    if (this.includeName) params.append('include_name', 'true');
    if (!this.includeLogo) params.append('include_logo', 'false');
    
    if (this.jobDescription && this.jobDescription.trim()) {
      params.append('job_description', this.jobDescription.trim());
    }
    
    if (forceRegenerate) {
      params.append('force_regenerate', 'true');
    }
    
    const apiBaseUrl = this.getApiBaseUrl();
    const url = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat?${params.toString()}`;
    
    console.log('🔄 Starting async reformat...');
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to start reformatting');
    }
    
    const result = await response.json();
    
    if (result.status === 'processing') {
      // Poll for completion
      await this.pollForCompletion();
    }
    
  } catch (error) {
    console.error('❌ Generation failed:', error);
    this.$emit('error', error.message);
  } finally {
    this.isGenerating = false;
  }
},

async pollForCompletion() {
  const apiBaseUrl = this.getApiBaseUrl();
  const statusUrl = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat/status`;
  
  const params = new URLSearchParams();
  params.append('id_type', this.idType); 
  if (this.jobDescription && this.jobDescription.trim()) {
    params.append('job_description', this.jobDescription.trim());
  }
  
  const maxAttempts = 90;  // 3 minutes max (90 * 2s)
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, 2000));  // Wait 2s
    
    try {
      const response = await fetch(`${statusUrl}?${params.toString()}`);
      const status = await response.json();
      
      console.log(`📊 ${status.message} (${status.progress}%)`);
      // ← ADD THESE TWO LINES HERE:
      this.progressPercent = status.progress || 0;
      this.progressMessage = status.message || 'Processing...';
      
      if (status.status === 'completed') {
        console.log('✅ Loading result...');
        await this.loadCachedResult();
        break;
      } else if (status.status === 'failed') {
        throw new Error(status.message || 'Reformat failed');
      }
      
    } catch (error) {
      console.error('❌ Status check failed:', error);
      throw error;
    }
    
    attempts++;
  }
  
  if (attempts >= maxAttempts) {
    throw new Error('Timed out after 3 minutes');
  }
},

async loadCachedResult() {
  const params = new URLSearchParams();
  params.append('id_type', this.idType); 
  params.append('format', 'html');
  
  if (this.jobDescription && this.jobDescription.trim()) {
    params.append('job_description', this.jobDescription.trim());
  }
  
  const apiBaseUrl = this.getApiBaseUrl();
  const url = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat?${params.toString()}`;
  
  const response = await fetch(url);
  const html = await response.text();
  
  this.reformattedHtml = html;
  this.cachedHtml = html;
  
  console.log('✅ HTML loaded');
  this.$emit('generated');
},
 // NEW: Toggle edit mode
 toggleEditMode() {
    this.isEditing = !this.isEditing;
    
    if (this.isEditing) {
      this.enableIframeEditing();
    } else {
      this.disableIframeEditing();
    }
  },
  
// NEW: Toggle page break preview
togglePageBreakPreview() {
    this.showPageBreaks = !this.showPageBreaks;
    
    if (this.showPageBreaks) {
      this.calculateAndShowPageBreaks();
    } else {
      this.hidePageBreaks();
    }
  },

  // NEW: Calculate where page breaks occur
  calculateAndShowPageBreaks() {
    try {
      const iframe = this.$refs.previewFrame;
      if (!iframe || !iframe.contentDocument) return;
      
      const doc = iframe.contentDocument;
      const body = doc.body;
      
      // A4 page dimensions at 96 DPI
      // 297mm = 11.69 inches = 1123 pixels at 96 DPI
      const PAGE_HEIGHT = 1123;
      
      // Get total content height
      const contentHeight = body.scrollHeight;
      
      // Calculate number of pages
      const numPages = Math.ceil(contentHeight / PAGE_HEIGHT);
      
      console.log(`📄 Content height: ${contentHeight}px`);
      console.log(`📄 Number of pages: ${numPages}`);
      
      // Remove existing page break markers
      doc.querySelectorAll('.page-break-marker').forEach(el => el.remove());
      
      // Insert visual markers at each page break
      this.pageBreakPositions = [];
      
      for (let i = 1; i < numPages; i++) {
        const breakPosition = i * PAGE_HEIGHT;
        this.pageBreakPositions.push(breakPosition);
        
        // Create visual marker
        const marker = doc.createElement('div');
        marker.className = 'page-break-marker';
        marker.style.cssText = `
          position: absolute;
          top: ${breakPosition}px;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, 
            transparent, 
            #dc3545 10%, 
            #dc3545 90%, 
            transparent
          );
          z-index: 9999;
          pointer-events: none;
        `;
        
        // Add page number indicator
        const label = doc.createElement('div');
        label.className = 'page-break-label';
        label.textContent = `Page ${i} | Page ${i + 1}`;
        label.style.cssText = `
          position: absolute;
          top: ${breakPosition - 12}px;
          left: 50%;
          transform: translateX(-50%);
          background: #dc3545;
          color: white;
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: bold;
          z-index: 10000;
          pointer-events: none;
          white-space: nowrap;
        `;
        
        // Ensure body has position: relative for absolute positioning
        if (body.style.position !== 'relative' && body.style.position !== 'absolute') {
          body.style.position = 'relative';
        }
        
        body.appendChild(marker);
        body.appendChild(label);
      }
      
      // Highlight elements that would be split across pages
      this.highlightSplitElements(PAGE_HEIGHT);
      
      console.log(`✅ Added ${numPages - 1} page break markers`);
      
    } catch (error) {
      console.error('Failed to calculate page breaks:', error);
    }
  },

  // NEW: Highlight elements that are split by page breaks
  highlightSplitElements(pageHeight) {
    const iframe = this.$refs.previewFrame;
    if (!iframe || !iframe.contentDocument) return;
    
    const doc = iframe.contentDocument;
    
    // Find all job entries, sections, etc.
    const elements = doc.querySelectorAll('.job, .experience-item, .education-item, section, .section');
    
    elements.forEach(el => {
      const rect = el.getBoundingClientRect();
      const elementTop = el.offsetTop;
      const elementBottom = elementTop + el.offsetHeight;
      
      // Check if element crosses a page boundary
      for (const breakPos of this.pageBreakPositions) {
        if (elementTop < breakPos && elementBottom > breakPos) {
          // Element is split!
          el.style.outline = '2px dashed #ffc107';
          el.style.outlineOffset = '2px';
          el.style.backgroundColor = 'rgba(255, 193, 7, 0.1)';
          
          // Add warning badge
          const warning = doc.createElement('span');
          warning.className = 'split-warning';
          warning.innerHTML = '<i class="fas fa-exclamation-triangle"></i> Split across pages';
          warning.style.cssText = `
            display: inline-block;
            background: #ffc107;
            color: #000;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: bold;
            margin-left: 8px;
            vertical-align: middle;
          `;
          
          // Add to first child (usually the title)
          const firstText = el.querySelector('h3, h4, .job-title, strong');
          if (firstText && !firstText.querySelector('.split-warning')) {
            firstText.appendChild(warning);
          }
          
          break;
        }
      }
    });
  },

  // NEW: Hide page break markers
  hidePageBreaks() {
    try {
      const iframe = this.$refs.previewFrame;
      if (!iframe || !iframe.contentDocument) return;
      
      const doc = iframe.contentDocument;
      
      // Remove all markers
      doc.querySelectorAll('.page-break-marker, .page-break-label, .split-warning').forEach(el => el.remove());
      
      // Remove highlight styling
      doc.querySelectorAll('[style*="outline"]').forEach(el => {
        el.style.outline = '';
        el.style.outlineOffset = '';
        el.style.backgroundColor = '';
      });
      
      this.pageBreakPositions = [];
      
      console.log('✅ Removed page break markers');
      
    } catch (error) {
      console.error('Failed to hide page breaks:', error);
    }
  },

  // NEW: Insert manual page break at cursor position
//   insertPageBreakAtCursor() {
//   try {
//     const iframe = this.$refs.previewFrame;
//     if (!iframe || !iframe.contentDocument) return;
    
//     const doc = iframe.contentDocument;
//     const selection = doc.getSelection();
    
//     if (!selection.rangeCount) {
//       alert('Please click where you want to insert a page break');
//       return;
//     }
    
//     const range = selection.getRangeAt(0);
    
//     // Create page break div with EXPLICIT CSS for PDF
//     const pageBreak = doc.createElement('div');
//     pageBreak.className = 'manual-page-break';
//     pageBreak.setAttribute('style', `
//       page-break-after: always !important;
//       break-after: page !important;
//       -webkit-column-break-after: always !important;
//       display: block !important;
//       height: 0 !important;
//       margin: 20px 0 !important;
//       border-top: 2px dashed #667eea;
//       position: relative;
//       clear: both !important;
//     `);  // ← Use setAttribute instead of style.cssText
    
//     // Add label (only visible in preview, not in PDF)
//     const label = doc.createElement('span');
//     label.className = 'page-break-label-preview';
//     label.textContent = 'Page Break (Manual)';
//     label.style.cssText = `
//       position: absolute;
//       top: -10px;
//       left: 50%;
//       transform: translateX(-50%);
//       background: #667eea;
//       color: white;
//       padding: 2px 8px;
//       border-radius: 3px;
//       font-size: 10px;
//       font-weight: bold;
//     `;
//     pageBreak.appendChild(label);
    
//     // Insert at cursor
//     range.insertNode(pageBreak);
    
//     // Recalculate page breaks
//     this.calculateAndShowPageBreaks();
    
//     console.log('✅ Inserted manual page break with explicit CSS');
    
//   } catch (error) {
//     console.error('Failed to insert page break:', error);
//     alert('Failed to insert page break. Please try again.');
//   }
// },
// insertPageBreakAtCursor() {
//   // Find the closest .section element
//   const iframe = this.$refs.previewFrame;
//   const doc = iframe.contentDocument;
//   const selection = doc.getSelection();
  
//   if (!selection.rangeCount) {
//     alert('Please click inside a section where you want to insert a page break');
//     return;
//   }
  
//   // Find closest section
//   let currentElement = selection.anchorNode;
//   while (currentElement && !currentElement.classList?.contains('section')) {
//     currentElement = currentElement.parentElement;
//   }
  
//   if (!currentElement) {
//     alert('Could not find a section to insert page break');
//     return;
//   }
  
//   // Insert AFTER the section
//   const pageBreak = doc.createElement('div');
//   pageBreak.className = 'manual-page-break';
//   pageBreak.innerHTML = '<span class="page-break-label-preview">Page Break</span>';
  
//   currentElement.parentNode.insertBefore(pageBreak, currentElement.nextSibling);
  
//   console.log('✅ Inserted page break after section');
// },

// insertPageBreakAtCursor() {
//   const iframe = this.$refs.previewFrame;
//   const doc = iframe.contentDocument;
//   const selection = doc.getSelection();
  
//   if (!selection.rangeCount) {
//     alert('Please click inside a section where you want to insert a page break');
//     return;
//   }
  
//   // Find closest section
//   let currentElement = selection.anchorNode;
//   while (currentElement && !currentElement.classList?.contains('section')) {
//     currentElement = currentElement.parentElement;
//   }
  
//   if (!currentElement) {
//     alert('Could not find a section to insert page break');
//     return;
//   }
  
//   // Insert AFTER the section
//   const pageBreak = doc.createElement('div');
//   pageBreak.className = 'manual-page-break';
  
//   // Add visual styling (purple/blue dashed line with label)
//   pageBreak.style.cssText = `
//     page-break-after: always !important;
//     break-after: page !important;
//     -webkit-column-break-after: always !important;
//     display: block !important;
//     height: 0 !important;
//     margin: 20px 0 !important;
//     border-top: 2px dashed #667eea;
//     position: relative;
//     clear: both !important;
//   `;
  
//   // Add label
//   pageBreak.innerHTML = `
//     <span class="page-break-label-preview" style="
//       position: absolute;
//       top: -10px;
//       left: 50%;
//       transform: translateX(-50%);
//       background: #667eea;
//       color: white;
//       padding: 2px 8px;
//       border-radius: 3px;
//       font-size: 10px;
//       font-weight: bold;
//     ">Page Break (Manual)</span>
//   `;
  
//   currentElement.parentNode.insertBefore(pageBreak, currentElement.nextSibling);
  
//   console.log('✅ Inserted manual page break after section');
  
//   // Visual feedback
//   pageBreak.scrollIntoView({ behavior: 'smooth', block: 'center' });
// },


insertPageBreakAtCursor() {
  const iframe = this.$refs.previewFrame;
  const doc = iframe.contentDocument;
  const selection = doc.getSelection();
  
  if (!selection.rangeCount) {
    alert('Please click where you want to insert a page break');
    return;
  }
  
  const range = selection.getRangeAt(0);
  
  // Create page break element
  const pageBreak = doc.createElement('div');
  pageBreak.className = 'manual-page-break';
  
  // Add visual styling
  pageBreak.style.cssText = `
    page-break-after: always !important;
    break-after: page !important;
    display: block !important;
    height: 0 !important;
    margin: 20px 0 !important;
    border-top: 2px dashed #667eea;
    position: relative;
    clear: both !important;
  `;
  
  // Add label
  pageBreak.innerHTML = `
    <span class="page-break-label-preview" style="
      position: absolute;
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      background: #667eea;
      color: white;
      padding: 2px 8px;
      border-radius: 3px;
      font-size: 10px;
      font-weight: bold;
    ">Page Break</span>
  `;
  
  // Insert at cursor position
  range.deleteContents();
  range.insertNode(pageBreak);
  
  // Move cursor after page break
  range.setStartAfter(pageBreak);
  range.setEndAfter(pageBreak);
  selection.removeAllRanges();
  selection.addRange(range);
  
  console.log('✅ Inserted page break at cursor');
  
  // Scroll to page break
  pageBreak.scrollIntoView({ behavior: 'smooth', block: 'center' });
},
  // NEW: Remove all manual page breaks
  removeAllPageBreaks() {
    try {
      const iframe = this.$refs.previewFrame;
      if (!iframe || !iframe.contentDocument) return;
      
      const doc = iframe.contentDocument;
      
      // Remove all manual breaks
      const breaks = doc.querySelectorAll('.manual-page-break');
      breaks.forEach(el => el.remove());
      
      console.log(`✅ Removed ${breaks.length} manual page breaks`);
      
      // Recalculate
      this.calculateAndShowPageBreaks();
      
    } catch (error) {
      console.error('Failed to remove page breaks:', error);
    }
  },



  // NEW: Make iframe content editable
  onIframeLoad() {
    if (this.isEditing) {
      this.enableIframeEditing();
    }

    // If page breaks were shown, recalculate after content loads
    if (this.showPageBreaks) {
      // Small delay to let content render
      setTimeout(() => {
        this.calculateAndShowPageBreaks();
      }, 100);
    }
  },
  

 // NEW: Execute formatting command on iframe content
 execCommand(command, value = null) {
    try {
      const iframe = this.$refs.previewFrame;
      if (!iframe || !iframe.contentDocument) {
        console.error('Iframe not ready');
        return;
      }

      const doc = iframe.contentDocument;
      
      // Focus the iframe first
      iframe.contentWindow.focus();
      
      // Execute the command
      doc.execCommand(command, false, value);
      
      console.log(`✅ Applied formatting: ${command}`);
    } catch (error) {
      console.error('Failed to apply formatting:', error);
    }
  },

  // NEW: Change font size
  changeFontSize(event) {
    const size = event.target.value;
    if (size) {
      this.execCommand('fontSize', size);
      event.target.value = ''; // Reset dropdown
    }
  },

  // NEW: Change text color
  changeTextColor(event) {
    const color = event.target.value;
    this.execCommand('foreColor', color);
  },

  // NEW: Remove all formatting from selection
  removeFormatting() {
    this.execCommand('removeFormat');
    this.execCommand('unlink'); // Also remove links
  },

  enableIframeEditing() {
    try {
      const iframe = this.$refs.previewFrame;
      if (!iframe || !iframe.contentDocument) return;
      
      const doc = iframe.contentDocument;
      const body = doc.body;
      
      if (body) {
        // Make entire body editable
        body.contentEditable = 'true';
        body.style.cursor = 'text';
        
        // Add visual indicator that it's editable
        body.style.outline = '2px solid #667eea';
        body.style.outlineOffset = '4px';
        
        // Enable design mode for better editing
        doc.designMode = 'on'; // ← ADD THIS
      }
    } catch (error) {
      console.error('Failed to enable editing:', error);
    }
  },
  
  disableIframeEditing() {
    try {
      const iframe = this.$refs.previewFrame;
      if (!iframe || !iframe.contentDocument) return;
      
      const doc = iframe.contentDocument;
      const body = doc.body;
      
      if (body) {
        body.contentEditable = 'false';
        body.style.cursor = 'default';
        body.style.outline = 'none';
        
        doc.designMode = 'off'; // ← ADD THIS
        
        // Capture edited HTML
        this.reformattedHtml = doc.documentElement.outerHTML;
      }
    } catch (error) {
      console.error('Failed to disable editing:', error);
    }
  },
 
      // NEW: Convert HTML to readable text (simple strip-tags approach)
 htmlToReadable(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove unwanted elements
    temp.querySelectorAll('script, style, img, svg').forEach(el => el.remove());
    
    let text = '';
    
    // Walk through all elements
    const processNode = (node, level = 0) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const content = node.textContent.trim();
        if (content) {
          text += content + '\n';
        }
        return;
      }
      
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      
      const tag = node.tagName.toLowerCase();
      
      // Headers
      if (tag === 'h1') {
        text += '\n# ' + node.textContent.trim() + '\n\n';
        return; // Don't process children
      }
      if (tag === 'h2') {
        text += '\n## ' + node.textContent.trim() + '\n\n';
        return;
      }
      if (tag === 'h3') {
        text += '\n### ' + node.textContent.trim() + '\n\n';
        return;
      }
      
      // Paragraphs
      if (tag === 'p') {
        text += node.textContent.trim() + '\n\n';
        return;
      }
      
      // Lists
      if (tag === 'ul' || tag === 'ol') {
        node.querySelectorAll('li').forEach(li => {
          text += '• ' + li.textContent.trim() + '\n';
        });
        text += '\n';
        return;
      }
      
      // Divs with specific classes (job entries, skills, etc.)
      if (tag === 'div') {
        // Check if it has class indicating structure
        if (node.classList.contains('job') || 
            node.classList.contains('job-title') ||
            node.classList.contains('job-company') ||
            node.classList.contains('job-period')) {
          text += node.textContent.trim() + '\n';
          return;
        }
        
        // Check for skill tags
        if (node.classList.contains('skill') || 
            node.classList.contains('skill-tag')) {
          text += '• ' + node.textContent.trim() + '\n';
          return;
        }
      }
      
      // Spans with skill content
      if (tag === 'span' && 
          (node.classList.contains('skill') || 
           node.classList.contains('skill-tag'))) {
        text += '• ' + node.textContent.trim() + '\n';
        return;
      }
      
      // Strong/Bold
      if (tag === 'strong' || tag === 'b') {
        text += '**' + node.textContent.trim() + '** ';
        return;
      }
      
      // Break
      if (tag === 'br') {
        text += '\n';
        return;
      }
      
      // For other elements, process children
      node.childNodes.forEach(child => processNode(child, level + 1));
    };
    
    processNode(temp);
    
    // Clean up extra newlines
    return text.replace(/\n{3,}/g, '\n\n').trim();
  },

  // NEW: For now, just update the HTML directly (simpler approach)
  readableToHtml(text) {
    // Since editing HTML structure is complex, we'll just replace text content
    // while preserving the HTML structure
    
    const temp = document.createElement('div');
    temp.innerHTML = this.cachedHtml;
    
    // Split text into lines
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    
    let lineIdx = 0;
    
    // Walk through HTML and update text content
    const updateNode = (node) => {
      if (lineIdx >= lines.length) return;
      
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.trim() && lineIdx < lines.length) {
          // Get the next non-markdown line
          let newText = lines[lineIdx];
          // Skip markdown syntax
          newText = newText.replace(/^#+\s*/, '');  // Remove # headers
          newText = newText.replace(/^•\s*/, '');    // Remove bullets
          newText = newText.replace(/^\*\*(.*?)\*\*/, '$1');  // Remove bold
          
          if (newText) {
            node.textContent = newText;
            lineIdx++;
          }
        }
        return;
      }
      
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      
      const tag = node.tagName.toLowerCase();
      
      // Update headers
      if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
        if (lineIdx < lines.length) {
          let line = lines[lineIdx];
          line = line.replace(/^#+\s*/, '');
          node.textContent = line;
          lineIdx++;
        }
        return;
      }
      
      // Update paragraphs
      if (tag === 'p') {
        if (lineIdx < lines.length) {
          let line = lines[lineIdx];
          line = line.replace(/^•\s*/, '');
          node.textContent = line;
          lineIdx++;
        }
        return;
      }
      
      // Update list items
      if (tag === 'li') {
        if (lineIdx < lines.length) {
          let line = lines[lineIdx];
          line = line.replace(/^•\s*/, '');
          node.textContent = line;
          lineIdx++;
        }
        return;
      }
      
      // Process children
      node.childNodes.forEach(child => updateNode(child));
    };
    
    updateNode(temp);
    
    return temp.innerHTML;
  },

  async regenerate() {
      console.log('🔄 REGENERATE button clicked');
      
      // if (!confirm('Regenerate the resume? This will incur processing costs and replace the current version.')) {
      //   console.log('❌ User cancelled regeneration');
      //   return;
      // }
      
      // console.log('✅ User confirmed regeneration');
      // this.reformattedHtml = null;
      // this.cachedHtml = null;
      // this.isEditing = false;
      
      // // Pass true to force regeneration
      // await this.generateReformat(true);
      // Just reset to show the form again
      this.reformattedHtml = null;
      this.cachedHtml = null;
      this.isEditing = false;
    },

  //     async exportPDF() {
  //   this.isExporting = true;
    
  //   try {
  //     // If in edit mode, capture current HTML from iframe
  //     if (this.isEditing) {
  //       const iframe = this.$refs.previewFrame;
  //       if (iframe && iframe.contentDocument) {
  //         this.reformattedHtml = iframe.contentDocument.documentElement.outerHTML;
  //       }
  //     }
      
  //     const apiBaseUrl = this.getApiBaseUrl();
  //     const url = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat/save`;
      
  //     console.log('🚀 Exporting PDF...');
      
  //     const response = await fetch(url, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ html: this.reformattedHtml })
  //     });
      
  //     if (!response.ok) {
  //       throw new Error('Failed to export PDF');
  //     }
      
  //     // Download PDF
  //     const blob = await response.blob();
  //     const downloadUrl = window.URL.createObjectURL(blob);
  //     const link = document.createElement('a');
  //     link.href = downloadUrl;
  //     link.download = `resume_${this.candidateId}_reformatted.pdf`;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(downloadUrl);
      
  //     console.log('✅ PDF exported');
  //     this.$emit('export-complete');
  //     this.close();
      
  //   } catch (error) {
  //     console.error('❌ Export failed:', error);
  //     this.$emit('error', error.message);
  //   } finally {
  //     this.isExporting = false;
  //   }
  // },
  
//   async exportPDF() {
//   this.isExporting = true;
  
//   try {
//     // ALWAYS capture current HTML from iframe (includes page breaks, edits, etc.)
//     const iframe = this.$refs.previewFrame;
//     if (iframe && iframe.contentDocument) {
//       this.reformattedHtml = iframe.contentDocument.documentElement.outerHTML;
//       console.log('📄 Captured HTML from iframe');
//     } else {
//       console.warn('⚠️ Could not access iframe, using cached HTML');
//     }
    
//     // Log to verify page breaks are included
//     if (this.reformattedHtml.includes('manual-page-break')) {
//       console.log('✅ HTML contains manual page breaks');
//     } else {
//       console.log('⚠️ No manual page breaks found in HTML');
//     }
    
//     const apiBaseUrl = this.getApiBaseUrl();
//     const url = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat/save`;
    
//     console.log('🚀 Exporting PDF...');
    
//     const response = await fetch(url, {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ html: this.reformattedHtml })
//     });
    
//     if (!response.ok) {
//       throw new Error('Failed to export PDF');
//     }
    
//     // Download PDF
//     const blob = await response.blob();
//     const downloadUrl = window.URL.createObjectURL(blob);
//     const link = document.createElement('a');
//     link.href = downloadUrl;
//     link.download = `resume_${this.candidateId}_reformatted.pdf`;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//     window.URL.revokeObjectURL(downloadUrl);
    
//     console.log('✅ PDF exported');
//     this.$emit('export-complete');
//     this.close();
    
//   } catch (error) {
//     console.error('❌ Export failed:', error);
//     this.$emit('error', error.message);
//   } finally {
//     this.isExporting = false;
//   }
// },

async exportPDF() {
  this.isExporting = true;
  
  try {
    // Capture HTML from iframe
    const iframe = this.$refs.previewFrame;
    if (!iframe || !iframe.contentDocument) {
      throw new Error('Cannot access iframe');
    }
    
    let html = iframe.contentDocument.documentElement.outerHTML;
    console.log('📄 Captured HTML from iframe');
    
    // ========================================
    // CLEAN UP: Remove ALL preview artifacts
    // ========================================
    
    // 1. Remove contenteditable attributes
    html = html.replace(/contenteditable="[^"]*"/gi, '');
    
    // 2. Remove outline and cursor styles from body
    html = html.replace(/<body[^>]*style="[^"]*"[^>]*>/gi, '<body>');
    
    // 3. Remove page break preview markers (the red lines and labels)
    html = html.replace(/<div class="page-break-marker"[^>]*>.*?<\/div>/gi, '');
    html = html.replace(/<div class="page-break-label"[^>]*>.*?<\/div>/gi, '');
    
    // 4. Remove split warnings (yellow highlights and warning badges)
    html = html.replace(/<span class="split-warning"[^>]*>.*?<\/span>/gi, '');
    
    // 5. Remove yellow outline styles from elements
    html = html.replace(/style="[^"]*outline: rgb\(255, 193, 7\)[^"]*"/gi, '');
    html = html.replace(/style="[^"]*background-color: rgba\(255, 193, 7[^"]*"/gi, '');
    
    // 6. Clean up manual page breaks - remove the preview label inside them
    html = html.replace(/<span class="page-break-label-preview"[^>]*>.*?<\/span>/gi, '');
    
    // 7. Remove any VSCode or browser-injected styles
    html = html.replace(/style="--vsc-domain:[^"]*"/gi, '');
    
    // 8. Clean up empty style attributes
    html = html.replace(/\s+style=""\s*/gi, ' ');
    
    console.log('🧹 Cleaned HTML (removed preview artifacts)');
    
    // Count manual page breaks (if any)
    const pageBreakCount = (html.match(/class="manual-page-break"/g) || []).length;
    console.log(`📊 Manual page breaks: ${pageBreakCount}`);
    
    // ========================================
    // Send to backend
    // ========================================
    
    const apiBaseUrl = this.getApiBaseUrl();
    const url = `${apiBaseUrl}/api/v1/export_resume/${this.candidateId}/reformat/save?id_type=${this.idType}`;
    
    console.log('🚀 Exporting PDF...');
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html })
    });
    
    if (!response.ok) {
      throw new Error('Failed to export PDF');
    }
    
    // Download PDF
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `resume_${this.candidateId}_reformatted.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    console.log('✅ PDF exported');
    this.$emit('export-complete');
    this.close();
    
  } catch (error) {
    console.error('❌ Export failed:', error);
    this.$emit('error', error.message);
  } finally {
    this.isExporting = false;
  }
},
      getApiBaseUrl() {
        if (process.env.NODE_ENV === 'development') {
          return process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000';
        }
        return '';
      },
      
      close() {
        this.isEditing = false;

        this.$emit('close');
      }
    }
  };
  </script>
  
  <style scoped>
  /* Reuse same styles as ResumeEditor.vue */
  .reformatter-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }
  
  .reformatter-modal {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 1200px;
    height: 90vh;  /* ← Make modal full height */
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  
  .reformatter-header {
    padding: 20px 24px;
    border-bottom: 1px solid #e5e5e5;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .reformatter-header h2 {
    margin: 0;
    font-size: 20px;
    color: #333;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  }
  
  .close-btn:hover {
    background: #f5f5f5;
    color: #333;
  }
  
  .reformatter-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    display: flex;  /* ← NEW: Flex to let preview grow */
    flex-direction: column;  /* ← NEW */
  }
  
  
  /* Loading State */
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  
  .loading-spinner {
    width: 60px;
    height: 60px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .loading-text {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-top: 20px;
  }
  
  .loading-subtext {
    font-size: 14px;
    color: #666;
    margin-top: 5px;
  }
  
  /* Input State */
  .input-state {
    max-width: 800px;
    margin: 0 auto;
  }
  
  .form-section {
    margin-bottom: 30px;
    color: #333;

  }
  
  .form-section h3 {
    font-size: 16px;
    color: #333;
    margin-bottom: 8px;
  }
  
  .help-text {
    font-size: 13px;
    color: #000000;
    margin-bottom: 12px;
  }
  
  .job-description-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
  }
  
  .job-description-textarea:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 14px;
    cursor: pointer;
    color: #333;

  }
  
  .checkbox-label input[type="checkbox"] {
    cursor: pointer;
  }
  
  /* Preview State */

  .preview-state {
    flex: 1;  /* ← NEW: Take all available space */
    display: flex;
    flex-direction: column;
    min-height: 0;  /* ← Important for Firefox */
  }
  
  .preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  
  .preview-header h3 {
    margin: 0;
    font-size: 16px;
    color: #333;
  }
  
  .preview-actions {
    display: flex;
    gap: 8px;
  }
  

  
  /* Edit Mode Indicator */
.edit-hint {
    margin-top: 12px;
    padding: 12px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #856404;
  }
  
  .edit-hint i {
    color: #ffc107;
  }
  
  /* Preview iframe - make sure it's sized correctly */
  .html-preview {
    flex: 1;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
    min-height: 600px;
    height: calc(90vh - 300px);
  }
  
  .preview-iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
  
  .html-editor-toggle {
    margin-top: 16px;
  }
  
  .html-editor-toggle summary {
    cursor: pointer;
    font-size: 13px;
    color: #666;
    padding: 8px;
    background: #f5f5f5;
    border-radius: 4px;
  }
  
  .html-editor {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    margin-top: 8px;
  }
  
  /* Footer */
  .reformatter-footer {
    padding: 16px 24px;
    border-top: 1px solid #e5e5e5;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
  
  /* Buttons */
  .btn-primary,
  .btn-secondary {
    padding: 10px 20px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
  }
  
  .btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
  
  .btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .btn-secondary {
    background: #f5f5f5;
    color: #333;
  }
  
  .btn-secondary:hover {
    background: #e5e5e5;
  }
  
  .btn-large {
    width: 100%;
    justify-content: center;
    padding: 14px 20px;
    font-size: 16px;
  }
  /* Simple Editor Section */
.simple-editor-section {
    margin-top: 16px;
  }
  
  .editor-toggle {
    margin-bottom: 12px;
  }
  
  .btn-toggle-editor {
    padding: 10px 16px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #333;
    transition: all 0.2s;
  }
  
  .btn-toggle-editor:hover {
    background: #e5e5e5;
    border-color: #667eea;
  }
  
  .btn-toggle-editor i {
    color: #667eea;
  }
  
  .simple-text-editor {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 12px;
    background: #fafafa;
  }
  
  .editor-help {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    margin-bottom: 12px;
    font-size: 13px;
    color: #856404;
  }
  
  .editor-help i {
    color: #ffc107;
  }
  
  .simple-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.6;
    background: white;
    color:#333;
    resize: vertical;
  }
  
  .simple-textarea:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  .progress-container {
    text-align: center;
    padding: 40px 20px;
  }
  
  .progress-bar {
    width: 100%;
    height: 8px;
    background: #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 16px;
  }
  
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    transition: width 0.3s ease;
    border-radius: 4px;
  }
  
  .progress-text {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
  }
  
  .progress-percent {
    font-size: 24px;
    font-weight: bold;
    color: #667eea;
  }
  
  /* Optional: Add animation */
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  .progress-fill {
    animation: pulse 2s ease-in-out infinite;
  }

/* Rich Text Toolbar */
.rich-text-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.toolbar-group {
  display: flex;
  gap: 4px;
  align-items: center;
}

.toolbar-btn {
  padding: 8px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  color: #495057;
  font-size: 14px;
}

.toolbar-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.toolbar-btn:active {
  background: #dee2e6;
}

.toolbar-btn i {
  font-size: 14px;
}

.toolbar-separator {
  width: 1px;
  height: 24px;
  background: #dee2e6;
  margin: 0 8px;
}

.toolbar-select {
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  color: #495057;
}

.toolbar-select:hover {
  border-color: #adb5bd;
}

.toolbar-color-picker {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.toolbar-color-picker:hover {
  border-color: #adb5bd;
}

.toolbar-color-picker i {
  margin-right: 6px;
  color: #495057;
}

.color-input {
  border: none;
  width: 30px;
  height: 20px;
  cursor: pointer;
}

/* Update edit hint */
.edit-hint {
  padding: 12px;
  background: #e7f3ff;
  border-left: 4px solid #667eea;
  color: #495057;
  font-size: 14px;
  margin-top: 12px;
  border-radius: 4px;
}

.edit-hint i {
  margin-right: 8px;
  color: #667eea;
}




/* Page Break Toolbar */
.page-break-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  margin-bottom: 16px;
}

.toolbar-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #856404;
  flex: 1;
}

.toolbar-info i {
  color: #ffc107;
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
}

/* Page break styling in iframe (injected via JS above) */
/* These are just documentation - actual styles are in JS */
/*
.page-break-marker {
  position: absolute;
  height: 2px;
  background: red;
  z-index: 9999;
}

.page-break-label {
  position: absolute;
  background: #dc3545;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  z-index: 10000;
}

.manual-page-break {
  page-break-after: always;
  break-after: page;
  border-top: 2px dashed #667eea;
}

.split-warning {
  background: #ffc107;
  color: #000;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
}
*/
  </style>